/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Asset,
  AssetMediaType,
  AssetOption,
  AssetUtils,
  Ducks,
  EntryPoint,
  FolderPurchaseOption,
  OptionUtils,
  PurchaseOption,
  PurchaseOptionType,
  Stream,
  SubscriptionErrors,
  TimeUtils,
  WatchOption,
  WatchOptionTv,
  WatchOptionType,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { ActionCreators as PlayerActionCreators, WatchRequestParams } from 'store/ducks/player';
import { useLocationWithState } from 'hooks/useLocationWithState';
import { useHistory, useParams } from 'react-router-dom';
import { StoreState } from 'store/store';
import * as PI from 'store/ducks/programInfo';
import * as Recordings from 'core/store/ducks/recordings/singleRecordings';
import { PrivateRoutePath, PRIVATE_DEFAULT_ROUTE } from 'routes/paths';
import { Hero, Modal, Pin, ProgrammeInfo, Rail, SeasonsWrapper, Spinner } from 'ui/components';
import { PiRailUi } from 'store/ducks/programInfo/data';
import { AssetActionButton } from 'types/components/ActionButtonsGroup';
import Player from 'ui/components/Player';
import MainSection from 'types/MainSection';
import { ParentContext } from 'store/ducks/folderInfo';
import translate from 'services/i18n/translate';
import * as Gdpr from 'core/store/ducks/gdpr';
import WatchTvWrapper from 'ui/components/WatchTv/WatchTvWrapper';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import OptInRail from 'ui/components/recommendations/OptInRail';
import * as ManagePin from 'store/ducks/settings/managePin';
import { ButtonOption } from 'types/components/SelectOptions';
import { useProgramInfoAnalytics } from 'hooks/useAnalytics';
import { addErrorEventByEntryPoint } from 'store/ducks/analytics/systemEvent';
import { useRecordingOptions } from 'hooks/useRecordingsOptions';
import { useRouter } from '../../../hooks/useRouter';

interface PathParams {
  section: MainSection;
  assetId: string;
  assetType: string;
  entryPoint?: EntryPoint;
}

interface LocationState {
  entryAsset: Asset;
  forceAutoPlay: boolean;
  restartIfAvailable?: boolean;
  entryLocation: Location;
}

interface OptionsModalProps {
  title: string;
  subtitle: string;
  options: ButtonOption[] | undefined;
  buttons: ButtonOption[];
}

interface ConfirmationModalProps {
  message: string;
  option: AssetOption;
  callBack: (option: AssetOption) => void;
}

const renderRelatedRails = (
  onCardClick: (asset: Asset, menuItemId?: number) => void,
  isRecommEnabled: boolean,
  rails?: PiRailUi[]
) => {
  if (isRecommEnabled && !rails?.length) {
    return (
      <ColdStartRail
        railId="relatedVod"
        className="no-background"
        railName={translate.formatString(translate['programInfo.related.vod'], '').toString()}
        railBackgroundImageName={RailBackgroundImageName.RELATED_RECOMM}
        railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
      />
    );
  }

  return rails?.map((rail) => (
    <div key={rail.key}>
      <Rail
        railId={rail.key}
        menuItemId={rail.menuItem?.id}
        className="no-background"
        railName={rail.title}
        assets={rail.assets}
        onCardClick={onCardClick}
      />
    </div>
  ));
};

const getActiveSeason = (seasons: PiRailUi[], asset?: Asset) => {
  return seasons.find((season) => season.assets.find((el) => el.id === asset?.id))?.key;
};

const getAssetActionButtons = (
  options: AssetOption[],
  actionButtonCallback: (option: AssetOption) => void
): AssetActionButton[] => {
  return options.map((option) => ({ ...option, onClick: () => actionButtonCallback(option) }));
};

const getMessage = (purchaseOption: PurchaseOption) => {
  const line1 =
    purchaseOption?.purchaseOptionType === PurchaseOptionType.BUY
      ? translate['modal.purchaseRentalSuccessMessage3']
      : translate['modal.purchaseRentalSuccessMessage1'];
  const line2 = purchaseOption.asset?.title;
  let line3 = '';
  // TODO: endDate is not available yet in the product price, get it from somewhere else
  if (purchaseOption.productPrice.endDate) {
    const expirationdate = TimeUtils.toBadgeDateTimeFormat(TimeUtils.toEpoch(purchaseOption.productPrice.endDate));
    line3 = translate.formatString(translate['modal.purchaseRentalSuccessMessage2'], { expirationdate }).toString();
  }
  return `${line1}\n${line2}\n${line3}`;
};

export default () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [inWatchingMode, setInWatchingMode] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsModalProps, setOptionsModalProps] = useState<OptionsModalProps>();
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalButtons, setResultModalButtons] = useState<ButtonOption[]>([]);
  const [showWatchTvWrapper, setShowWatchTvWrapper] = useState(false);
  const [isWaitingGdprConfirmation, setIsWaitingGdprConfirmation] = useState(false);
  const [showPurchasePinModal, setShowPurchasePinModal] = useState(false);
  const location = useLocationWithState();
  const router = useRouter();
  const history = useHistory();
  const dispatch = useDispatch();

  // get params passed in the route
  const { assetId, assetType } = useParams<PathParams>();
  const { entryPoint, menuItemId } = router.queryParams;

  const { entryAsset, forceAutoPlay, restartIfAvailable, entryLocation } =
    (location.state as LocationState | undefined) || {}; // may be empty if the app is reloaded
  const autoPlayFired = useRef(false);

  const entryMenuItem = useSelector((state: StoreState) => {
    if (menuItemId !== undefined) {
      return Ducks.Menu.Selectors.getMenuItemById(state, menuItemId);
    }
    return undefined;
  });

  // build a unique context for the initial params
  // useRef will create the context just once even between renders
  const ctxRef = useMemo<PI.PiContext>(() => {
    return {
      entryAssetId: entryAsset?.id || Number(assetId),
      entryAssetMediaType: assetType,
      entryAsset,
      entryPoint,
      entryMenuItem,
    };
  }, [assetId, assetType, entryAsset, entryMenuItem, entryPoint]);

  const isRecommEnabled = useSelector(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const isGdprConsent = useSelector(Gdpr.Selectors.hasAgreedToConsent);
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);

  // available for assets related with channels: ex: PROGRAMS, RECORDING
  const relatedChannel = useSelector((state: StoreState) => PI.Selectors.data.relatedChannel(state, ctxRef));
  // available if it is a live asset
  const nextPrograms = useSelector((state: StoreState) => PI.Selectors.data.nextPrograms(state, ctxRef));
  const options = useSelector((state: StoreState) => PI.Selectors.data.options(state, ctxRef));
  // listen store for the asset infos
  const asset = useSelector((state: StoreState) => PI.Selectors.data.asset(state, ctxRef));
  const seasons = useSelector((state: StoreState) => PI.Selectors.data.seasons(state, ctxRef));
  const related = useSelector((state: StoreState) => PI.Selectors.data.related(state, ctxRef));
  // Get all recordings, not agregated
  const allRecordings = useSelector((state: StoreState) => Recordings.Selectors.getRecordings(state));

  const foldersRelated = useSelector((state: StoreState) => PI.Selectors.data.foldersRelated(state, ctxRef));
  const loadInfoError = useSelector((state: StoreState) => PI.Selectors.actions.loadInfo.getError(state, ctxRef));
  const isAssetInfoProcessed = useSelector((state: StoreState) =>
    PI.Selectors.actions.loadInfo.isProcessed(state, ctxRef)
  );

  // #region Unsubscribed Channel Error
  const isPlaybackUnavailable = useCallback(() => {
    if (relatedChannel && AssetUtils.isChannel(relatedChannel)) {
      return !AssetUtils.isPlayableChannel(relatedChannel);
    }
    return false;
  }, [relatedChannel]);

  useEffect(() => {
    if (isPlaybackUnavailable()) {
      const unsubcribedError = XError.fromCode(SubscriptionErrors.generic.channelNotSubscribed);
      // Set Message
      setErrorMessage(unsubcribedError.translation);
    }
  }, [isPlaybackUnavailable]);
  // #endregion

  useProgramInfoAnalytics(asset);

  // "onMount" load PI info
  useEffect(() => {
    setInWatchingMode(false);
    autoPlayFired.current = false;
    setShowWatchTvWrapper(false);
    dispatch(PI.Creators.loadInfo.request(ctxRef));

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    // destructor
    return () => {
      dispatch(PI.Creators.clear.request(ctxRef));
    };
  }, [ctxRef, dispatch]);

  // handling player errors
  const onPlaybackError = useCallback(() => {
    setInWatchingMode(false);
  }, []);

  const onPlaybackHidden = useCallback(() => {
    setInWatchingMode(false);
  }, []);

  // run option state
  const runOptionError = useSelector((state: StoreState) => PI.Selectors.actions.runOption.getError(state, ctxRef));

  // handling no content error
  useEffect(() => {
    if (!loadInfoError) {
      return;
    }

    switch (ctxRef.entryPoint) {
      case EntryPoint.RECORDINGS:
      case EntryPoint.SCHEDULED_RECORDINGS:
      case EntryPoint.PURCHASES:
      case EntryPoint.WATCH_NEXT:
        router.replace({ routePath: PrivateRoutePath.MYTV });
        break;
      case EntryPoint.CLU:
      case EntryPoint.TV_GUIDE:
        router.replace({ routePath: PrivateRoutePath.WATCHTV });
        break;
      case EntryPoint.ONDEMAND:
        router.replace({ routePath: PrivateRoutePath.ONDEMAND });
        break;
      default:
        router.replace({ routePath: PRIVATE_DEFAULT_ROUTE });
    }
  }, [ctxRef.entryPoint, loadInfoError, router]);

  // Confirmation Modal
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalProps | undefined>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // handling new errors executing options
  useEffect(() => {
    if (runOptionError) {
      setErrorMessage(runOptionError.translation);
      addErrorEventByEntryPoint(runOptionError, ctxRef.entryPoint);
    }
  }, [ctxRef.entryPoint, runOptionError]);

  const onErrorDismissed = () => {
    setErrorMessage('');
  };

  const closeModal = () => {
    setShowOptionsModal(false);
  };

  const getChildOptions = <T extends AssetOption>(option: T, callback: (option: T) => void) => {
    const watchOptionsButtons = option.children?.map((childOption) => {
      const modalButton: ButtonOption = {
        id: childOption.key,
        text: childOption.title,
        icon: {
          uri: childOption.channel?.logo?.color?.uri || '',
          alt: childOption.channel?.title || '',
        },
        onClick: () => callback(childOption as T), // assuming that children have the same type as parent
      };

      return modalButton;
    });
    return watchOptionsButtons;
  };

  // #region WATCH OPTIONS
  const watchCallback = useCallback(
    (option: WatchOption) => {
      setShowOptionsModal(false);
      if (option.asset) {
        const appOption = option as PI.AppWatchOption;
        if (appOption.mode === PI.WatchMode.RESTART && inWatchingMode) {
          dispatch(PlayerActionCreators.restartRequest());
        } else {
          const watchParams: WatchRequestParams = { asset: option.asset };
          if (appOption.provider === PI.WatchOptionProvider.LIVE) {
            watchParams.asset = (option as WatchOptionTv).channel;
          } else {
            watchParams.progress = appOption.mode === PI.WatchMode.RESTART ? 0 : option.progress;
            if (OptionUtils.isWatchVod(option)) {
              watchParams.stream = option.stream;
            }
          }
          dispatch(PlayerActionCreators.watchRequest(watchParams));
          setInWatchingMode(true);
        }
      }
    },
    [dispatch, inWatchingMode]
  );

  const watchOptionsCallback = useCallback(
    (option: WatchOption) => {
      setShowOptionsModal(false);
      if (option.asset) {
        const appOption = option as PI.AppWatchOption;
        // If the option is "watch live" and not yet in the live PI, jump to the live PI
        if (
          appOption.provider === PI.WatchOptionProvider.LIVE &&
          ctxRef.entryAssetMediaType !== AssetMediaType.LINEAR
        ) {
          if (option.channel) {
            router.replace({
              asset: option.channel,
              state: { forceAutoPlay: true },
              entryPoint: EntryPoint.TV_GUIDE,
              menuItemId,
            });
          }
        } else {
          watchCallback(option);
        }
      }
    },
    [ctxRef.entryAssetMediaType, menuItemId, router, watchCallback]
  );

  const watchOptions = (option: WatchOption) => {
    if (option.children?.length) {
      const childOptions = getChildOptions(option, watchOptionsCallback);
      const title = translate['buttons.watchOptions'];
      const subtitle = translate.modalSubTitleWatchOptions;
      const buttons = [{ id: 'modalButtonCancel', text: translate['modal.button.cancel'], onClick: closeModal }];

      setOptionsModalProps({
        options: childOptions,
        title,
        subtitle,
        buttons,
      });
      setShowOptionsModal(true);
    } else {
      watchOptionsCallback(option);
    }
  };
  // #endregion

  // auto-play in live
  useEffect(() => {
    // execute this when the options are calculated and while not watch option was executed
    if (ctxRef.entryAssetMediaType === AssetMediaType.LINEAR && !autoPlayFired.current) {
      const option = options.find(
        (op) =>
          OptionUtils.isWatchPlay(op) &&
          op.watchOptionType === WatchOptionType.TV_LIVE &&
          (op as PI.AppWatchOption).mode !== PI.WatchMode.RESTART
      );

      if (option) {
        watchCallback(option as WatchOption);
        autoPlayFired.current = true;
      }
    } else if (forceAutoPlay && !autoPlayFired.current) {
      let option = options.find(
        (op) => OptionUtils.isWatchPlay(op) && (op as PI.AppWatchOption).mode !== PI.WatchMode.RESTART
      );

      if (option) {
        if (restartIfAvailable) {
          option = { ...option, progress: 0 };
        }
        watchCallback(option as WatchOption);
        autoPlayFired.current = true;
      }
    }
  }, [options, watchCallback, forceAutoPlay, ctxRef.entryAssetMediaType, restartIfAvailable]);

  // #region PURCHASE OPTIONS
  const purchaseSuccess = useSelector((state: StoreState) => Ducks.Transactions.Selectors.isSuccess(state));
  const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState('');
  const [purchaseOption, setPurchaseOption] = useState<PurchaseOption | undefined>();
  const resultModalCloseButton = useRef({
    id: 'resultModalCloseButton',
    text: translate['modal.button.close'],
    onClick: () => setShowResultModal(false),
  });

  const purchaseWatchNow = useCallback(
    (option: PurchaseOption, stream: Stream) => {
      setShowResultModal(false);
      if (option.asset) {
        const watchParams: WatchRequestParams = { asset: option.asset, stream };
        dispatch(PlayerActionCreators.watchRequest(watchParams));
        setInWatchingMode(true);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (purchaseSuccess) {
      if (purchaseOption) {
        setPurchaseSuccessMessage(getMessage(purchaseOption));
        let watchNowButton: ButtonOption[] = [];
        const purchasedStream = purchaseOption.asset?.streams.find(
          (stream) => purchaseOption.productPrice.fileId === stream.id
        );
        if (purchaseOption.asset && purchasedStream) {
          watchNowButton = [
            {
              id: 'watchNowButton',
              text: translate['modal.button.watch'],
              onClick: () => purchaseWatchNow(purchaseOption, purchasedStream),
            },
          ];
        }
        setResultModalButtons([...watchNowButton, resultModalCloseButton.current]);
      }
      dispatch(PI.Creators.runOption.request(ctxRef));
    }
  }, [ctxRef, dispatch, purchaseOption, purchaseSuccess, purchaseWatchNow]);

  const continueWithPurchase = (option: PurchaseOption) => {
    setResultModalButtons([resultModalCloseButton.current]);
    setShowResultModal(true);
    if (OptionUtils.isPurchaseOption(option)) {
      setPurchaseOption(option);
      dispatch(PI.Creators.runOption.request(ctxRef, option));
    }
  };

  const purchase = (option: AssetOption) => {
    setShowOptionsModal(false);
    if (OptionUtils.isPurchaseOption(option)) {
      setPurchaseOption(option);
      if (defaultUser?.parentalValidation && defaultUser?.purchasePinEnabled) {
        dispatch(ManagePin.ActionCreators.pinReset());
        setShowPurchasePinModal(true);
      } else {
        continueWithPurchase(option);
      }
    }
  };

  const onPurchasePinModalCancel = () => {
    setShowPurchasePinModal(false);
    dispatch(ManagePin.ActionCreators.pinReset());
  };

  const onPurchasePinConfirmation = () => {
    setShowPurchasePinModal(false);
    dispatch(ManagePin.ActionCreators.pinReset());
    if (purchaseOption) {
      continueWithPurchase(purchaseOption);
    }
  };

  const purchaseOptions = (option: PurchaseOption) => {
    if (option.children) {
      const childOptions = getChildOptions(option, purchase);
      const title =
        option.purchaseOptionType === PurchaseOptionType.BUY
          ? translate.modalTitlePurchaseOptions
          : translate.modalTitleRentalOptions;
      const subtitle = translate.modalSubTitleVideoQuality;
      const buttons = [{ id: 'modalButtonCancel', text: translate['modal.button.cancel'], onClick: closeModal }];

      setOptionsModalProps({
        options: childOptions,
        title,
        subtitle,
        buttons,
      });
      setShowOptionsModal(true);
    } else {
      purchase(option);
    }
  };
  // #endregion

  // #region RECORDING OPTIONS
  const goToNextEpisodeOrExitPIAfterRecordingDeletion = useCallback(() => {
    if (runOptionError) {
      return;
    }

    const entryPointCtx = ctxRef.entryPoint;
    if (
      entryPointCtx === EntryPoint.RECORDINGS ||
      entryPointCtx === EntryPoint.SCHEDULED_RECORDINGS ||
      entryPointCtx === EntryPoint.WATCH_NEXT
    ) {
      if (asset && AssetUtils.isRecording(asset) && AssetUtils.hasValidSeriesId(asset)) {
        // Using slice function, prevents from mutating the original array
        // Filter Recordings that are actual recordings and not schedule recordings.
        // Filter also the current asset so the next recording is not the same as the current recording
        const actualRecordings = allRecordings.slice().filter((e) => {
          return e.seriesId === asset.seriesId && TimeUtils.isBeforeNow(e.endDate) && e.id !== asset.id;
        });
        const recordingsByDate = actualRecordings.sort((a, b) => TimeUtils.compare(b.endDate, a.endDate));

        // The newest next recording is the first of the array
        const nextRecording = recordingsByDate.length ? recordingsByDate[0] : undefined;

        // Reload page with the next recording available
        if (nextRecording) {
          router.replace({
            asset: nextRecording,
            state: { forceAutoPlay: false },
            entryPoint: entryPointCtx,
            menuItemId,
          });
          return;
        }
      }

      if (entryLocation) {
        history.replace(entryLocation);
      } else {
        router.goBack();
      }
    }
  }, [allRecordings, asset, entryLocation, router, runOptionError, menuItemId, ctxRef.entryPoint, history]);

  const runOptionIsProcessed = useSelector((state: StoreState) =>
    PI.Selectors.actions.runOption.isProcessed(state, ctxRef)
  );

  const dispatchRecordingOption = useCallback(
    (option: AssetOption) => {
      dispatch(PI.Creators.runOption.request(ctxRef, option));
    },
    [ctxRef, dispatch]
  );

  const recordingOptions = useRecordingOptions(
    setConfirmationModal,
    setShowConfirmationModal,
    runOptionIsProcessed,
    dispatchRecordingOption,
    goToNextEpisodeOrExitPIAfterRecordingDeletion
  );

  // #endregion

  // FOLDER PURCHASE OPTIONS
  const folderPurchaseOptions = (option: FolderPurchaseOption) => {
    if (option.asset) {
      let parentContext: ParentContext | undefined;

      if (AssetUtils.isFolder(option.asset)) {
        parentContext = {
          entryAsset: asset,
          entryPoint: entryPoint || EntryPoint.ONDEMAND,
          relatedChannel,
        };
      }

      router.replace({
        asset: option.asset,
        state: { parentContext },
        entryPoint: EntryPoint.SUBSCRIPTIONS,
        menuItemId,
      });
    }
  };

  // OPTIONS
  const actionButtonsCallback = (option: AssetOption) => {
    if (OptionUtils.isWatch(option)) {
      watchOptions(option);
      return;
    }

    if (OptionUtils.isWishlist(option)) {
      dispatch(PI.Creators.runOption.request(ctxRef, option));
      return;
    }

    if (OptionUtils.isPurchaseOption(option)) {
      purchaseOptions(option);
      return;
    }

    if (OptionUtils.isRecording(option)) {
      recordingOptions.runRecordingOption(option);

      return;
    }

    if (OptionUtils.isFolderPurchaseOption(option)) {
      folderPurchaseOptions(option);
      return;
    }

    if (OptionUtils.isMoreInfoOption(option)) {
      return;
    }

    if (OptionUtils.isExploreFolderOption(option)) {
      return;
    }

    if (OptionUtils.isHowToPurchase(option)) {
      // return;
    }
  };

  // Result Modal

  const onCardClick = (assetClicked: Asset, menuItemId_?: number, assetEntryPoint?: EntryPoint) => {
    let parentContext: ParentContext | undefined;

    if (AssetUtils.isFolder(assetClicked)) {
      parentContext = {
        entryAsset: asset,
        entryPoint: entryPoint || EntryPoint.ONDEMAND,
        relatedChannel,
      };
    }

    router.push({
      asset: assetClicked,
      state: { parentContext },
      entryPoint: assetEntryPoint,
      menuItemId: menuItemId_,
    });
  };

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const activeAsset = asset;
  const backgroundImage = activeAsset?.backdrop?.uri;

  const memoPlayer = useMemo(() => {
    if (inWatchingMode) {
      return <Player onError={onPlaybackError} onHide={onPlaybackHidden} setShowWrapper={setShowWatchTvWrapper} />;
    }
    return <></>;
  }, [inWatchingMode, onPlaybackError, onPlaybackHidden]);

  return (
    <div className="pi-page-container" id="ProgramInfo">
      <Modal isShowing={showPurchasePinModal}>
        <Pin closeModal={onPurchasePinModalCancel} onPinCallback={onPurchasePinConfirmation} />
      </Modal>

      <Modal
        title={optionsModalProps?.title}
        subtitle={optionsModalProps?.subtitle}
        setIsShowing={setShowOptionsModal}
        isShowing={showOptionsModal}
        bodyButtons={optionsModalProps?.options}
        buttons={optionsModalProps?.buttons}
        customClassName="watchtv-options"
      />

      <Modal
        title={purchaseSuccess ? translate['modal.title.success'] : translate['modal.title.processing']}
        message={purchaseSuccessMessage}
        setIsShowing={setShowResultModal}
        isShowing={errorMessage === '' && showResultModal}
        buttons={resultModalButtons}
      />

      <Modal
        title={translate['modal.title.confirmation']}
        message={confirmationModal?.message}
        setIsShowing={setShowConfirmationModal}
        isShowing={showConfirmationModal}
        buttons={[
          {
            id: 'modalButtonConfirm',
            text: translate['modal.button.confirm'],
            onClick: () => confirmationModal?.callBack(confirmationModal.option),
          },
          {
            id: 'modalButtonCancel',
            text: translate['modal.button.cancel'],
            onClick: () => setShowConfirmationModal(false),
          },
        ]}
      />

      <Modal
        title={translate['modal.title.error']}
        message={errorMessage}
        buttons={[{ id: 'modalButtonOk', text: translate.modalButtonOk, onClick: () => onErrorDismissed() }]}
        isShowing={errorMessage !== ''}
        setIsShowing={() => onErrorDismissed()}
      />

      {memoPlayer}
      {!inWatchingMode ? <Hero className="pi-page-hero" backgroundImage={backgroundImage} /> : <></>}
      {showWatchTvWrapper && <WatchTvWrapper overlay setShowWrapper={setShowWatchTvWrapper} />}

      <ProgrammeInfo
        channel={relatedChannel}
        asset={asset}
        actionButtons={getAssetActionButtons(options, actionButtonsCallback)}
        liveEventNextPrograms={nextPrograms}
        ctx={ctxRef}
      />

      {!isAssetInfoProcessed ? (
        <div className="layout-spinner-container component-inner">
          <Spinner className="pi-spinner" isActive isEmbedded />
        </div>
      ) : null}

      {seasons.length ? (
        <SeasonsWrapper
          data={seasons}
          menuItemId={menuItemId}
          activeSeason={getActiveSeason(seasons, activeAsset)}
          onCardClick={(assetClicked, menuItemId_) => onCardClick(assetClicked, menuItemId_, EntryPoint.SEASONS)}
        />
      ) : null}

      {isRecommEnabled && (!isGdprConsent || (isGdprConsent && isWaitingGdprConfirmation)) ? (
        <OptInRail
          railId="programInfoRelated"
          railName={translate.formatString(translate['programInfo.related.vod'], '').toString()}
          railBackgroundImageName={RailBackgroundImageName.RELATED_RECOMM}
          railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
          optInClicked
          continueClicked={() => setIsWaitingGdprConfirmation(false)}
          onOptInClick={() => setIsWaitingGdprConfirmation(true)}
        />
      ) : (
        renderRelatedRails(
          (assetClicked, menuItemId_) => onCardClick(assetClicked, menuItemId_, EntryPoint.RELATED),
          isRecommEnabled,
          related
        )
      )}

      {foldersRelated
        ? foldersRelated.map((folder) => (
            <div key={folder.key}>
              <Rail
                railId={folder.key}
                // menuItemId => don't send menu item here since the folders menu item is not part of the original menu
                className="no-background"
                railName={folder.title}
                assets={folder.assets}
                onCardClick={(assetClicked) => onCardClick(assetClicked, undefined, EntryPoint.RELATED)}
              />
            </div>
          ))
        : null}
    </div>
  );
};
